// localizationUtils.js - Centralized localization utilities

/**
 * Helper function to get localized messages
 * Backward compatible - supports both old and new call signatures:
 *   - getLocalizedMessage(intl, messageId)
 *   - getLocalizedMessage(intl, messageId, values)           // old: values as 3rd arg
 *   - getLocalizedMessage(intl, messageId, defaultMessage)   // new: defaultMessage as 3rd arg
 *   - getLocalizedMessage(intl, messageId, defaultMessage, values) // new: both
 * 
 * @param {Object} intl - The intl object from useIntl hook
 * @param {string} messageId - The message ID to localize
 * @param {string|Object} defaultMessageOrValues - Optional default message (string) or values (object)
 * @param {Object} valuesIfDefaultProvided - Optional values when defaultMessage is provided
 * @returns {string} - The localized message
 */
export const getLocalizedMessage = (intl, messageId, defaultMessageOrValues, valuesIfDefaultProvided) => {
  // Determine if 3rd arg is defaultMessage (string) or values (object)
  let defaultMessage = '';
  let values = {};
  
  if (typeof defaultMessageOrValues === 'string') {
    // New pattern: getLocalizedMessage(intl, messageId, defaultMessage, values?)
    defaultMessage = defaultMessageOrValues;
    values = valuesIfDefaultProvided || {};
  } else if (typeof defaultMessageOrValues === 'object' && defaultMessageOrValues !== null) {
    // Old pattern: getLocalizedMessage(intl, messageId, values)
    values = defaultMessageOrValues;
  }
  
  try {
    return intl.formatMessage({ id: messageId, defaultMessage }, values);
  } catch (error) {
    // If there's an error with interpolation, try to get the message without values
    // and provide a fallback
    try {
      const message = intl.formatMessage({ id: messageId, defaultMessage });
      // If the message contains interpolation placeholders, replace them with a fallback
      return message.replace(/\{(\w+)\}/g, (match, key) => {
        return values[key] !== undefined ? values[key] : `[${key}]`;
      });
    } catch (fallbackError) {
      // If all else fails, return the defaultMessage or messageId as fallback
      return defaultMessage || messageId;
    }
  }
};

/**
 * Resolves an API responseCode to a localized message only when a matching locale key exists.
 * Returns an empty string when the responseCode is missing or not present in locale files.
 */
export const resolveApiResponseCodeMessage = (intl, responseCode) => {
  if (responseCode === undefined || responseCode === null || responseCode === '') {
    return '';
  }
  const codeStr = String(responseCode);
  if (intl?.messages?.[codeStr]) {
    return intl.formatMessage({ id: codeStr });
  }
  return '';
};